import React, { useCallback, useEffect, useState } from 'react';
import { Clickable, Text, FocusOn } from '@moda/om';
import classNames from 'classnames';
import { remToUnitlessPx, useNode } from '@moda/portal-stanchions';
import { useUrlParams } from '../../../hooks/useUrlParams';
import { useLook } from '../useLook';
import { LooksCarouselMobileModalVariants } from './LooksCarouselMobileModalVariants';

import './LooksCarouselMobileModal.scss';

const INITIAL_SCROLL_MARGIN_REM = 12;
const INITIAL_SCROLL_MARGIN = remToUnitlessPx(INITIAL_SCROLL_MARGIN_REM.toString());

const DELAY_MS = 500;

enum Mode {
  BeforeInitialization,
  Initialized,
  Closing
}

interface Props {
  onClose: () => void;
}

export const LooksCarouselMobileModal: React.FC<Props> = ({ onClose }) => {
  const {
    params: { look: lookId }
  } = useUrlParams();
  const { numberOfVariants } = useLook(lookId);

  const { ref, node } = useNode();
  const [mode, setMode] = useState(Mode.BeforeInitialization);

  useEffect(() => {
    if (!node) return;

    if (mode !== Mode.Closing) return;

    const timeout = setTimeout(onClose, DELAY_MS);
    return () => clearTimeout(timeout);
  }, [mode, node, onClose]);

  const handleInitialScroll = useCallback(() => {
    if (!node) return;

    if (mode !== Mode.BeforeInitialization) return;

    node.scrollBy({ top: INITIAL_SCROLL_MARGIN, behavior: 'smooth' });
    setMode(Mode.Initialized);
  }, [mode, node]);

  const handleScroll = useCallback(() => {
    if (!node) return;

    const { scrollTop } = node;

    if (mode === Mode.Initialized && scrollTop < 0) {
      setMode(Mode.Closing);
    }
  }, [mode, node]);

  return (
    <FocusOn onActivation={handleInitialScroll} scrollLock>
      <div
        className={classNames('LooksCarouselMobileModal', {
          'LooksCarouselMobileModal--closing': mode === Mode.Closing
        })}
        onScroll={handleScroll}
        ref={ref}
      >
        <Clickable
          className="LooksCarouselMobileModal__offset"
          onClick={() => mode === Mode.Initialized && setMode(Mode.Closing)}
        />

        <div className="LooksCarouselMobileModal__content-drag-symbol">
          <svg
            width="63"
            height="7"
            viewBox="0 0 63 7"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.5 7C0.5 3.13401 3.63401 0 7.5 0H55.5C59.366 0 62.5 3.13401 62.5 7H0.5Z"
              fill="#E6E6E6"
            />
          </svg>
        </div>

        <div className="LooksCarouselMobileModal__content-header">
          <Text treatment="bold1">{`${numberOfVariants} ${
            numberOfVariants === 1 ? 'Piece' : 'Pieces'
          } in this look`}</Text>
        </div>

        <div
          className={classNames('LooksCarouselMobileModal__content', {
            'LooksCarouselMobileModal__content--closing': mode === Mode.Closing
          })}
        >
          <LooksCarouselMobileModalVariants />
        </div>
      </div>
    </FocusOn>
  );
};
